import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  ArrowRight,
  CheckCircle,
  ArrowLeft,
  X,
  Plus,
  Trash2,
  Save,
  Eye,
  Sparkles,
  BookOpen
} from 'lucide-react';
import { FiMousePointer } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import ProcessingOverlay from './ProcessingOverlay';

import QuestionsView, { transformJSON } from './QuestionsView';
import QuestionNavigator from './QuestionNavigator';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../auth/AuthContext';
import { useDispatch } from 'react-redux';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { gradeApi } from '../../../../api';
import { pdfToImage } from '../utils/pdfToImage';
import './styling/RubricViewer.scss';

const RubricViewer = ({
  activeTab,
  setActiveTab,
  isProcessing,
  loadingSteps,
  extractedQuestions,
  currentLoadingStep,
  pdfFiles,
  setExtractedQuestions,
  setProceed,
  onPublish, // New prop for parent component callback
  onSave, // New prop for parent component callback
  assignment_data // New prop for assignment data
}) => {
  const [totalPages, setTotalPages] = useState(1);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfLoadingProgress, setPdfLoadingProgress] = useState(0);
  const [hasShownInitialNotification, setHasShownInitialNotification] = useState(false);
  const [currentMissingSolution, setCurrentMissingSolution] = useState(null);
  const [isEditingMCQ, setIsEditingMCQ] = useState(false);
  const [advancedVisionEnabled, setAdvancedVisionEnabled] = useState(false);
  const [showVisionDisclosure, setShowVisionDisclosure] = useState(false);
  const [showAdvancedVisionWarning, setShowAdvancedVisionWarning] = useState(false);
  const [sparkleInterval, setSparkleInterval] = useState(null);
  const [generalInstructions, setGeneralInstructions] = useState('');
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const { class_id, assignment_id } = useParams();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate(); // Add navigate from useNavigate hook
  const questionsRef = useRef({});
  const pdfContainerRef = useRef(null);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);

  const fetchQuestionsFromAPI = async () => {
    setIsQuestionsLoading(true);
    try {
      console.log('Fetching questions directly from byob-questions endpoint');
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment_id}/byob-questions/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      if (response.data) {
        console.log('Successfully loaded questions from byob-questions');
        console.log(response.data);

        // Make sure we preserve max_weight values for each subquestion
        const processedData = Object.fromEntries(
          Object.entries(response.data).map(([key, question]) => {
            if (question.subquestions && question.subquestions.length > 0) {
              // Ensure max_weight is preserved for each subquestion
              question.subquestions = question.subquestions.map(sub => {
                // If max_weight exists in the response, use it
                // Otherwise fall back to weight or default to 1
                if (!sub.max_weight && sub.max_weight !== 0) {
                  sub.max_weight = sub.max_weight || 1;
                }
                return sub;
              });
            }
            return [key, question];
          })
        );

        setExtractedQuestions(processedData);
      } else {
        console.error('No question data returned from byob-questions endpoint');
        toast.error('Failed to load questions. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching questions from byob-questions:', error);
      toast.error('Failed to load questions. Please try again.');
    } finally {
      setIsQuestionsLoading(false);
    }
  };

  // Replace or add this useEffect to always fetch questions on component mount
  useEffect(() => {
    // Always fetch questions from the API directly when the component loads
    fetchQuestionsFromAPI();
  }, [assignment_id]); // Only re-fetch if assignment_id changes

  // Add effect to auto-save whenever extractedQuestions changes
  // This ensures that adding/deleting questions, subquestions, and MCQ options triggers saving
  const [isProceedingToNextStep, setIsProceedingToNextStep] = useState(false);

  useEffect(() => {
    // Skip saving on initial load, when questions are null, or when we're proceeding to next step
    if (extractedQuestions && !isQuestionsLoading && !isProceedingToNextStep) {
      // Use setTimeout to debounce frequent updates
      const saveTimeout = setTimeout(() => {
        // Auto-save is now only responsible for saving progress, not calling constructor
        autoSaveProgress();
        console.log('Auto-saved progress after questions were modified');
      }, 1000);

      return () => clearTimeout(saveTimeout);
    }
  }, [extractedQuestions, isQuestionsLoading, isProceedingToNextStep]);

  useEffect(() => {
    console.log('PDF files:', pdfFiles);
    const loadScrollablePdf = async () => {
      try {
        console.log('Loading PDF files:', pdfFiles);
        if (!pdfFiles) {
          console.error('pdfFiles is ndefined or null');
          setLoadingPdf(false);
          return;
        }

        let pdfUrl = pdfFiles.withSolutions || pdfFiles.withoutSolutions;

        // Handle different types of pdfUrl values
        console.log('Original PDF URL:', pdfUrl);

        // If pdfUrl is a string but not a base64 string, it's likely a direct URL
        if (typeof pdfUrl === 'string' && !pdfUrl.startsWith('data:')) {
          console.log('Using direct URL for PDF loading:', pdfUrl);
        }

        if (!pdfUrl) {
          console.error('No PDF URL available in pdfFiles');
          setLoadingPdf(false);
          return;
        }

        if (!pdfContainerRef.current) {
          console.error('PDF container reference is not available');
          setLoadingPdf(false);
          return;
        }

        console.log('Loading PDF from URL:', pdfUrl);
        setLoadingPdf(true);
        setPdfLoadingProgress(0);

        // Clear any existing content
        pdfContainerRef.current.innerHTML = '';

        // Always use pdfToImage for all PDF formats (base64 or URL)
        console.log('Using pdfToImage for PDF rendering:', typeof pdfUrl);

        const totalPdfPages = await pdfToImage({
          file: pdfUrl,
          scale: 1.5,
          container: pdfContainerRef.current,
          onProgress: (current, total) => {
            setPdfLoadingProgress(Math.round((current / total) * 100));
          },
          onComplete: total => {
            console.log(`PDF loaded with ${total} pages`);
            setTotalPages(total);
            setLoadingPdf(false);
          }
        });
      } catch (error) {
        console.error('Error loing PF:', error);
        setLoadingPdf(false);
        toast.error(`Failed to load PDF: ${error.message}`);
      }
    };

    // Only attempt to load if both the container ref and PDF files exist
    if (pdfContainerRef.current && pdfFiles) {
      loadScrollablePdf();
    }
  }, [pdfFiles, pdfContainerRef, activeTab, loadingSteps]); // Ad

  const uploadQuestionsAction = createAsyncThunk(
    'tpset/uploadQuestions',
    async ({ assignment_id, token, body }, thunkApi) => {
      try {
        const response = await gradeApi.post({
          path: `/api/assignment/${assignment_id}/constructor/`,
          body,
          config: { headers: { Authorization: `Bearer ${token}` } }
        });
        return response.data;
      } catch (error) {
        return thunkApi.rejectWithValue(
          error.response?.data?.error || error.message || 'An error occurred. Please try again.'
        );
      }
    }
  );

  const uploadQuestions = async (updatedQuestions = extractedQuestions) => {
    if (!updatedQuestions) {
      console.warn('Cannot upload null questions');
      return;
    }

    // Clear any previous constructor data to prevent stale data issues when switching between assignments
    // or when parsing new PDFs for the same assignment
    localStorage.removeItem('constructorData');

    // Auto-save progress before uploading questions
    autoSaveProgress();

    // Check if we have constructor data from localStorage (from merged questions)
    const constructorDataStr = localStorage.getItem('constructorData');
    let questionsToTransform = updatedQuestions;

    if (constructorDataStr) {
      try {
        const constructorData = JSON.parse(constructorDataStr);
        // Use the constructor data if available
        questionsToTransform = constructorData;
        // Clear the localStorage item after using it
        localStorage.removeItem('constructorData');
      } catch (error) {
        console.error('Error parsing constructor data:', error);
        // Fall back to the original questions if there's an error
        questionsToTransform = updatedQuestions;
      }
    }

    const questions2 = transformJSON(questionsToTransform);
    console.log(questions2);

    const payload = {
      exam_paper: questions2,
      instructions: 'Complete this assignment',
      model_instructions:
        "Grade the student's work carefully. Pay attention to their problem-solving approach, clarity of explanations, and correctness of calculations. Select the appropriate rubric item, based on the demonstrated understanding and effort.",
      feedback_instructions: 'detailed',
      show_points: true,
      hide_first_page: false,
      page: 0,
      allow_pregrade: false,
      bubble_sheet: false,
      preserve_questions: false // Flag to indicate that questions should not be deleted and recreated
    };

    await dispatch(
      uploadQuestionsAction({ assignment_id, token: user.session_token, body: payload })
    );
  };

  // Function to update assignment instructions
  const handleUpdateInstructions = instructions => {
    const updatedQuestions = { ...extractedQuestions };

    // Update or add the assignment instructions
    if (!updatedQuestions.instructions) {
      updatedQuestions.instructions = { text: instructions };
    } else {
      updatedQuestions.instructions.text = instructions;
    }

    setExtractedQuestions(updatedQuestions);
  };

  // Function to save current progress data
  const saveProgress = async targetStep => {
    try {
      // Use provided targetStep if available, otherwise use step 4 for RubricViewer
      const creationStep = targetStep !== undefined ? targetStep : 4;

      console.log('Saving progress with creation step:', creationStep);

      // Prepare progress data for saving
      const progressData = {
        creation_step: creationStep,
        // Include the extracted questions data and PDF references
        question_boxes: extractedQuestions ? JSON.stringify(extractedQuestions) : null,
        template_pdf_url: pdfFiles?.withoutSolutions || null,
        solution_pdf_url: pdfFiles?.withSolutions || null
      };

      // Save progress via API
      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/save-progress/`,
        body: progressData,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      localStorage.removeItem('rubricsGenerated');

      // If there's a parent callback, invoke it
      if (onSave && typeof onSave === 'function') {
        onSave(targetStep);
      }

      toast.success('Progress saved successfully!');
      return true;
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to save progress. Please try again.');
      return false;
    }
  };

  // Auto-save without showing toast messages
  const autoSaveProgress = targetStep => {
    const saveData = async () => {
      try {
        // Use provided targetStep if available, otherwise use step 4 for RubricViewer
        const creationStep = targetStep !== undefined ? targetStep : 4;

        const progressData = {
          creation_step: creationStep,
          question_boxes: extractedQuestions ? JSON.stringify(extractedQuestions) : null,
          template_pdf_url: pdfFiles?.withoutSolutions || null,
          solution_pdf_url: pdfFiles?.withSolutions || null
        };

        await gradeApi.post({
          path: `/api/assignment/${assignment_id}/save-progress/`,
          body: progressData,
          config: {
            headers: {
              Authorization: `Bearer ${user.session_token}`
            }
          }
        });
        console.log(`Auto-saved progress as step ${creationStep}`);

        // If there's a parent callback, invoke it
        if (onSave && typeof onSave === 'function') {
          onSave(targetStep);
        }
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    };

    saveData();
  };

  // Function to specifically save rubrics data to the backend
  const saveRubrics = async () => {
    try {
      if (!extractedQuestions) {
        toast.error('No questions data to save');
        return;
      }

      console.log('Saving rubrics with questions:', extractedQuestions);

      // Format the data for the save-rubrics endpoint
      const rubricData = {
        examPaper: Object.values(extractedQuestions).map(question => {
          const formattedQuestion = {
            ...question,
            subQuestions:
              question.subquestions?.map(sub => ({
                ...sub,
                rubricItems: sub.rubricItems || [],
                solution: sub.solution || '',
                // Important: Always use max_weight and ensure it's properly passed
                max_weight: sub.max_weight || 1
              })) || []
          };
          return formattedQuestion;
        })
      };

      console.log('Formatted rubric data:', rubricData);

      // Call the save-rubrics API endpoint
      const response = await gradeApi.post({
        path: `/api/assignment/${assignment_id}/save-rubrics/`,
        body: rubricData,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      toast.success('Rubrics saved successfully!');
      return true;
    } catch (error) {
      console.error('Error saving rubrics:', error);
      toast.error('Failed to save rubrics. Please try again.');
      return false;
    }
  };

  const handleBackClick = () => {
    // Save progress with step 3 (solution upload step) before going back
    autoSaveProgress(3);

    if (typeof setProceed === 'function') {
      setProceed(false);
    } else {
      // Fallback behavior if setProceed is not available
      console.warn('setProceed function is not available, using history.back()');
      window.history.back();
    }
    if (typeof setExtractedQuestions === 'function') {
      setExtractedQuestions(null);
    }
  };

  // Function to get all missing solutions
  const getMissingSolutions = questions => {
    if (!questions) {
      return [];
    }

    const missing = [];
    Object.entries(questions).forEach(([id, question]) => {
      // Check main MCQ questions
      if (question.type === 'mcq' || question.questionType === 'mcq') {
        // Check if correctAnswer array exists and has at least one element
        const hasCorrectAnswer =
          Array.isArray(question.correctAnswer) && question.correctAnswer.length > 0;
        // Fallback to the old method if correctAnswer doesn't exist
        const hasCorrectOptionOldMethod =
          question.mcqOptions && question.mcqOptions.some(opt => opt.isCorrect);

        if (!hasCorrectAnswer && !hasCorrectOptionOldMethod) {
          missing.push({ id, subIndex: null, type: 'mcq', question });
        }
      } else if (!question.subquestions || question.subquestions.length === 0) {
        // For questions with no subquestions that aren't MCQs, require a solution
        if (!question.solution || !question.solution.trim()) {
          missing.push({ id, subIndex: null, type: 'freeform', question });
        }
      } else if (question.subquestions) {
        // Check each subquestion
        question.subquestions.forEach((sub, index) => {
          if (sub.type === 'mcq' || sub.questionType === 'mcq') {
            // Check if correctAnswer array exists and has at least one element
            const hasCorrectAnswer =
              Array.isArray(sub.correctAnswer) && sub.correctAnswer.length > 0;
            // Fallback to the old method if correctAnswer doesn't exist
            const hasCorrectOptionOldMethod =
              sub.mcqOptions && sub.mcqOptions.some(opt => opt.isCorrect);

            if (!hasCorrectAnswer && !hasCorrectOptionOldMethod) {
              missing.push({ id, subIndex: index, type: 'mcq', question: sub });
            }
          } else if (!sub.solution || !sub.solution.trim()) {
            // For freeform questions, still require a text solution
            missing.push({ id, subIndex: index, type: 'freeform', question: sub });
          }
        });
      }
    });
    return missing;
  };

  useEffect(() => {
    const missingSolutions = getMissingSolutions(extractedQuestions);
    if (missingSolutions.length > 0) {
      if (
        !currentMissingSolution ||
        !missingSolutions.some(
          sol =>
            sol.id === currentMissingSolution.id && sol.subIndex === currentMissingSolution.subIndex
        )
      ) {
        setCurrentMissingSolution(missingSolutions[0]);
      }
    } else {
      setCurrentMissingSolution(null);
      setIsEditingMCQ(false);
    }
  }, [extractedQuestions]);

  // Highlight all missing solutions permanently
  useEffect(() => {
    // First, remove highlights from all solution elements
    document.querySelectorAll('.highlight-missing-solution').forEach(el => {
      el.classList.remove('highlight-missing-solution');
    });

    // Then highlight all missing solutions
    const missingSolutions = getMissingSolutions(extractedQuestions);
    missingSolutions.forEach(solution => {
      const elementId = `solution-${solution.id}${solution.subIndex !== null ? `-${solution.subIndex}` : ''}`;
      const element = document.getElementById(elementId);
      if (element) {
        element.classList.add('highlight-missing-solution');
      }
    });

    // Scroll to current missing solution if it exists
    if (currentMissingSolution && !isEditingMCQ) {
      const elementId = `solution-${currentMissingSolution.id}${currentMissingSolution.subIndex !== null ? `-${currentMissingSolution.subIndex}` : ''}`;
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [extractedQuestions, currentMissingSolution, isEditingMCQ]);
  const getMissingSolutionsMessage = questions => {
    if (!questions) {
      return 'No questions have been extracted yet';
    }

    const missingSolutions = getMissingSolutions(questions);
    console.log('missingSolutions:', missingSolutions);

    console.log(missingSolutions);
    if (missingSolutions.length === 0) {
      return 'You cannot proceed at this time. This is likely a bug - please report to support@gradewiz.ai';
    }

    const formatSolution = sol => {
      return sol.subIndex !== null
        ? `Question ${sol.id}-${String.fromCharCode(97 + sol.subIndex)}`
        : `Question ${sol.id}`;
    };

    return `Please enter solutions for: ${missingSolutions.map(formatSolution).join(', ')}`;
  };

  const allQuestionsHaveSolutions = useMemo(() => {
    if (!extractedQuestions) {
      return false;
    }

    return Object.entries(extractedQuestions)
      .filter(([id]) => id !== 'instructions') // Skip the instructions when checking for solutions
      .every(([id, question]) => {
        if (question.type === 'mcq' || question.questionType === 'mcq') {
          // Check using correctAnswer array first
          const hasCorrectAnswer =
            Array.isArray(question.correctAnswer) && question.correctAnswer.length > 0;
          // Fallback to old method
          const hasCorrectOptionOldMethod =
            question.mcqOptions && question.mcqOptions.some(opt => opt.isCorrect);
          return hasCorrectAnswer || hasCorrectOptionOldMethod;
        }

        if (!question.subquestions || question.subquestions.length === 0) {
          // For questions with no subquestions that aren't MCQs, check if they have a solution
          return typeof question.solution === 'string' && question.solution.trim().length > 0;
        }

        return question.subquestions.every(sub => {
          if (sub.type === 'mcq' || sub.questionType === 'mcq') {
            // Check using correctAnswer array first
            const hasCorrectAnswer =
              Array.isArray(sub.correctAnswer) && sub.correctAnswer.length > 0;
            // Fallback to old method
            const hasCorrectOptionOldMethod =
              sub.mcqOptions && sub.mcqOptions.some(opt => opt.isCorrect);
            return hasCorrectAnswer || hasCorrectOptionOldMethod;
          }
          return typeof sub.solution === 'string' && sub.solution.trim().length > 0;
        });
      });
  }, [extractedQuestions]);

  console.log('allQuestionsHaveSolutions:', allQuestionsHaveSolutions);

  useEffect(() => {
    if (extractedQuestions && !hasShownInitialNotification && !allQuestionsHaveSolutions) {
      const message = getMissingSolutionsMessage(extractedQuestions);
      toast(message, {
        duration: 5000,
        style: {
          background: '#F3F4F6',
          color: '#374151',
          border: '1px solid #E5E7EB',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        },
        icon: '⚠️'
      });
      setHasShownInitialNotification(true);
    }
  }, [extractedQuestions, hasShownInitialNotification, allQuestionsHaveSolutions]);

  // Add manual save button handler
  const handleManualSave = () => {
    saveProgress(4); // Always save as step 4 when in RubricViewer
    saveRubrics(); // Also save the rubrics specifically
  };

  const handleProceedClick = async () => {
    if (!allQuestionsHaveSolutions) {
      const message = getMissingSolutionsMessage(extractedQuestions);
      toast(message, {
        duration: 4000,
        style: {
          background: '#F3F4F6',
          color: '#374151',
          border: '1px solid #E5E7EB',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        },
        icon: '⚠️'
      });
      return;
    }

    try {
      // Set flag to prevent auto-save from triggering while proceeding
      setIsProceedingToNextStep(true);

      // Only make a single call to save progress as step 6 (generate-rubric) before navigating
      // No need to call uploadQuestions as changes are already saved at this point
      await saveProgress(6);
      console.log('Saved progress as step 6 before proceeding to generate-rubric page');

      // Use React Router's navigate with a query parameter to inform generate-rubric page
      // this is a direct navigation from RubricViewer (to prevent redirection)
      navigate(
        `/tclasses/${class_id}/tassignments/${assignment_id}/create/rubric?from=rubricviewer`
      );
    } catch (error) {
      setIsProceedingToNextStep(false); // Reset flag if there's an error
      console.error('Error proceeding to generate-rubric page:', error);
      toast.error('Failed to proceed. Please try again.');
    }
  };

  // Initialize advanced vision state from assignment data
  useEffect(() => {
    console.log('assignment_data:', assignment_data);
    if (assignment_data?.advanced_vision !== undefined) {
      setAdvancedVisionEnabled(!!assignment_data.advanced_vision);
    }
    if (assignment_data?.model_instructions) {
      setGeneralInstructions(assignment_data.model_instructions);
    }
  }, [assignment_data]);

  // Function to update general instructions in the backend
  const handleUpdateGeneralInstructions = async instructions => {
    try {
      await gradeApi.post({
        path: `api/assignment/${assignment_id}/update-model-instructions`,
        body: { instructions }
      });

      // Update local state after successful API call
      setGeneralInstructions(instructions);
      toast.success('General instructions updated successfully');
    } catch (error) {
      console.error('Error updating general instructions:', error);
      toast.error(error.response?.data?.message || 'Failed to update general instructions');

      // If there was an error, revert to the previous value from assignment_data
      if (assignment_data?.model_instructions) {
        setGeneralInstructions(assignment_data.model_instructions);
      }
    }
  };

  // Add debounced save for general instructions
  useEffect(() => {
    if (generalInstructions !== undefined && assignment_data) {
      // Don't update if instructions haven't changed from initial load
      if (generalInstructions === assignment_data.model_instructions) {
        return;
      }

      const saveTimeout = setTimeout(() => {
        handleUpdateGeneralInstructions(generalInstructions);
      }, 1500); // 1.5 second debounce

      return () => clearTimeout(saveTimeout);
    }
  }, [generalInstructions]);

  const handleMoveQuestion = (questionIdToMove, direction) => {
    if (!extractedQuestions) {
      return;
    }

    let questionEntriesArray = Object.entries(extractedQuestions);
    const questionIndex = questionEntriesArray.findIndex(([id, _]) => id === questionIdToMove);

    if (questionIndex === -1) {
      console.error(`Question with id ${questionIdToMove} not found for moving.`);
      return;
    }

    let newEntriesArray = [...questionEntriesArray];
    let finalKeyToScrollTo = questionIdToMove; // Default, will be updated

    if (direction === 'up' && questionIndex > 0) {
      const temp = newEntriesArray[questionIndex][1];
      newEntriesArray[questionIndex][1] = newEntriesArray[questionIndex - 1][1];
      newEntriesArray[questionIndex - 1][1] = temp;
      finalKeyToScrollTo = questionEntriesArray[questionIndex - 1][0]; // Key of the destination slot
    } else if (direction === 'down' && questionIndex < newEntriesArray.length - 1) {
      const temp = newEntriesArray[questionIndex][1];
      newEntriesArray[questionIndex][1] = newEntriesArray[questionIndex + 1][1];
      newEntriesArray[questionIndex + 1][1] = temp;
      finalKeyToScrollTo = questionEntriesArray[questionIndex + 1][0]; // Key of the destination slot
    } else {
      return; // Cannot move further or invalid direction
    }

    const newExtractedQuestions = Object.fromEntries(newEntriesArray);
    setExtractedQuestions(newExtractedQuestions);

    // Scroll the moved question into view after the DOM updates
    requestAnimationFrame(() => {
      const elementId = `question-${finalKeyToScrollTo}`;
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  return (
    <div
      className="rubric-viewer-container"
      style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header with buttons */}
      <div
        className="proceed-section"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '1rem',
          borderBottom: '1px solid #e5e7eb',
          position: 'relative'
        }}
      >
        {/* Left section */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}
        >
          <button
            style={{
              background: 'white',
              color: '#4b5563',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onClick={handleBackClick}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
        </div>

        {/* Center section - REMOVED Advanced Vision toggle from here */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            zIndex: 1
          }}
        >
          {/* Page count indicator moved to center */}
          <div
            style={{
              display: 'flex',
              gap: '24px',
              alignItems: 'center',
              color: '#6b7280',
              fontSize: '14px',
              fontWeight: '500'
            }}
          ></div>
        </div>

        {/* Right section - Finalize Assignment button (replaced Proceed to Generate Rubrics) */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 2
          }}
        >
          <div className="total-points">
            Total:{' '}
            <span className="total-points-value">
              {Object.values(extractedQuestions || {})
                .reduce((total, question) => {
                  // Get main question weight or default to 0
                  let questionTotal = 0;

                  // Add subquestion weights
                  if (question.subquestions && question.subquestions.length > 0) {
                    questionTotal += question.subquestions.reduce((subTotal, sub) => {
                      // Ensure weight is a number to prevent string concatenation
                      const weight = Number(sub.weight) || 1;
                      return subTotal + weight;
                    }, 0);
                  }

                  return total + questionTotal;
                }, 0)
                .toFixed(1)}
            </span>{' '}
            points
          </div>
          <button
            className={`proceed-btn ${!allQuestionsHaveSolutions ? 'disabled' : ''}`}
            data-tooltip={
              !allQuestionsHaveSolutions ? getMissingSolutionsMessage(extractedQuestions) : ''
            }
            onClick={async () => {
              if (allQuestionsHaveSolutions) {
                // First, make sure to save all changes including the instructions
                await uploadQuestions(extractedQuestions);
                // Then call onPublish with the latest extractedQuestions which include the instructions
                onPublish(extractedQuestions);
              } else {
                const message = getMissingSolutionsMessage(extractedQuestions);
                toast(message, {
                  duration: 4000,
                  style: {
                    background: '#F3F4F6',
                    color: '#374151',
                    border: '1px solid #E5E7EB',
                    padding: '16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  },
                  icon: '⚠️'
                });
              }
            }}
          >
            <Save className="w-5 h-5" />
            <span>Finalize Assignment</span>
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div style={{ display: 'flex', flex: 1, height: 'calc(100vh - 70px)', overflow: 'hidden' }}>
        {isProcessing ? (
          <ProcessingOverlay loadingSteps={loadingSteps} currentStep={currentLoadingStep} />
        ) : extractedQuestions ? (
          <>
            {/* PDF Preview on the left - now scrollable */}
            <div
              ref={pdfContainerRef}
              style={{
                width: '50%',
                height: '100%',
                borderRight: '1px solid #e5e7eb',
                position: 'relative',
                overflowY: 'auto'
              }}
            >
              {loadingPdf && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 10
                  }}
                >
                  <div className="loading-spinner mb-4"></div>
                  <p style={{ color: '#4b5563' }}>Loading PDF: {pdfLoadingProgress}%</p>
                </div>
              )}
            </div>

            {/* Extracted Questions on the right */}
            <div style={{ width: '50%', height: '100%', overflowY: 'auto' }}>
              <div className="questions-view" style={{ padding: '1rem' }}>
                {/* Assignment Instructions Card */}
                <div className="question-card">
                  <div className="question-content">
                    <div className="solution-wrapper">
                      <div className="solution">
                        <div className="solution-header">
                          <BookOpen className="w-4 h-4" />
                          <span>General Instructions for Grading</span>
                        </div>
                        <div className="solution-content">
                          <textarea
                            className="solution-content-text"
                            placeholder="Add general instructions like: 'Don't penalize rounding errors', 'Students are in 6th grade taking a quiz', 'Be lenient with partial credit', etc."
                            value={generalInstructions}
                            rows={4}
                            onChange={e => setGeneralInstructions(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {isEditingMCQ && currentMissingSolution && currentMissingSolution.type === 'mcq' ? (
                  <MCQSolutionEditor
                    currentMissingSolution={currentMissingSolution}
                    extractedQuestions={extractedQuestions}
                    setExtractedQuestions={setExtractedQuestions}
                    onClose={() => setIsEditingMCQ(false)}
                  />
                ) : (
                  <QuestionsView
                    extractedQuestions={extractedQuestions}
                    setExtractedQuestions={setExtractedQuestions}
                    uploadQuestions={uploadQuestions}
                    currentMissingSolution={currentMissingSolution}
                    pdfPageCount={totalPages}
                    handleMoveQuestion={handleMoveQuestion}
                    solutionPdfUrl={pdfFiles?.withSolutions}
                    onEditMCQ={() => {
                      if (currentMissingSolution && currentMissingSolution.type === 'mcq') {
                        setIsEditingMCQ(true);
                      }
                    }}
                  />
                )}
              </div>
            </div>

            {/* Question Navigator sidebar */}
            {extractedQuestions && Object.keys(extractedQuestions).length > 0 && (
              <QuestionNavigator
                extractedQuestions={extractedQuestions}
                highlightedQuestion={currentMissingSolution}
              />
            )}
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <p>Loading questions...</p>
          </div>
        )}
      </div>
    </div>
  );
};

// MCQ Solution Editor Component
const MCQSolutionEditor = ({
  currentMissingSolution,
  extractedQuestions,
  setExtractedQuestions,
  onClose
}) => {
  const { id, subIndex, question } = currentMissingSolution;

  // Get the actual question from extractedQuestions
  const questionData =
    subIndex === null ? extractedQuestions[id] : extractedQuestions[id].subquestions[subIndex];

  // Initialize options if they don't exist or convert them to the right format
  const [options, setOptions] = useState(() => {
    if (questionData.mcqOptions && Array.isArray(questionData.mcqOptions)) {
      return questionData.mcqOptions;
    } else if (questionData.options && Array.isArray(questionData.options)) {
      // Convert from old format to new format
      return questionData.options.map((option, idx) => ({
        option: String.fromCharCode(65 + idx),
        text: option,
        isCorrect: questionData.correctAnswer?.includes(String.fromCharCode(65 + idx)) || false
      }));
    } else {
      // Create default options
      return [
        { option: 'A', text: '', isCorrect: false },
        { option: 'B', text: '', isCorrect: false },
        { option: 'C', text: '', isCorrect: false },
        { option: 'D', text: '', isCorrect: false }
      ];
    }
  });

  const updateOption = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setOptions(newOptions);
  };

  const addOption = () => {
    if (options.length >= 26) {
      return; // Limit to alphabet letters
    }

    const newOption = {
      option: String.fromCharCode(65 + options.length),
      text: '',
      isCorrect: false
    };
    setOptions([...options, newOption]);
  };

  const removeOption = index => {
    if (options.length <= 2) {
      return; // Maintain at least 2 options
    }
    const newOptions = options.filter((_, i) => i !== index);
    // Re-assign letters
    const updatedOptions = newOptions.map((opt, i) => ({
      ...opt,
      option: String.fromCharCode(65 + i)
    }));
    setOptions(updatedOptions);
  };

  const saveMCQSolution = () => {
    // Make sure at least one option is marked correct
    if (!options.some(opt => opt.isCorrect)) {
      toast.error('Please mark at least one option as correct');
      return;
    }

    const updatedQuestions = { ...extractedQuestions };

    if (subIndex === null) {
      // Update main question
      updatedQuestions[id] = {
        ...updatedQuestions[id],
        mcqOptions: options
      };
    } else {
      // Update subquestion
      const subquestions = [...updatedQuestions[id].subquestions];
      subquestions[subIndex] = {
        ...subquestions[subIndex],
        mcqOptions: options
      };
      updatedQuestions[id] = {
        ...updatedQuestions[id],
        subquestions
      };
    }

    setExtractedQuestions(updatedQuestions);
    onClose();
  };

  return (
    <div className="mcq-solution-editor">
      <div className="editor-header">
        <h2>Edit MCQ Solution</h2>
        <div className="question-info">
          <p className="question-id">
            Question {id}
            {subIndex !== null ? String.fromCharCode(97 + subIndex) : ''}
          </p>
          <p className="question-text">{questionData.questionText || questionData.text}</p>
        </div>
        <button className="close-btn" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      <div className="options-container">
        <h3>Mark the correct answer(s):</h3>
        {options.map((option, index) => (
          <div key={index} className="mcq-option">
            <div className="option-header">
              <div className="option-letter">{option.option}</div>
              <label className="correct-checkbox">
                <input
                  type="checkbox"
                  checked={option.isCorrect}
                  onChange={() => updateOption(index, 'isCorrect', !option.isCorrect)}
                />
                <span>Correct Answer</span>
              </label>
              {options.length > 2 && (
                <button className="delete-option" onClick={() => removeOption(index)}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>
            <input
              type="text"
              value={option.text}
              placeholder="Enter option text..."
              className="option-text-input"
              onChange={e => updateOption(index, 'text', e.target.value)}
            />
          </div>
        ))}

        <button className="add-option-btn" onClick={addOption}>
          <Plus size={16} /> Add Option
        </button>
      </div>

      <div className="controls">
        <button className="cancel-btn" onClick={onClose}>
          Cancel
        </button>
        <button className="save-btn" onClick={saveMCQSolution}>
          Save Solution
        </button>
      </div>
    </div>
  );
};

export default RubricViewer;
